import api from 'api';
import {get} from 'lodash';
import {useState, useCallback} from 'react';
import toast from 'react-hot-toast';
import {useMutation, useQuery} from 'react-query';
import {DataItem, ListResponse, Webhook, WebhookEvent} from 'types';

const getWebhookList = async () => {
  const {data} = await api.get<ListResponse<Webhook>>(
    '/integrations/webhook-configs/'
  );
  return data;
};

const getWebhookEvents = async () => {
  const {data} = await api.get<WebhookEvent[]>(
    '/integrations/webhook-configs/events/'
  );
  return data;
};

type CreateWebhookPayload = {
  name: string;
  url: string;
  description: string;
  events: string[];
};

const createWebhook = async (payload: CreateWebhookPayload) => {
  const {data} = await api.post<Webhook>(
    '/integrations/webhook-configs/',
    payload
  );
  return data;
};

type UpdateWebhookPayload = {
  id: string;
  name?: string;
  url?: string;
  description?: string;
  events?: string[];
  is_active?: boolean;
};

const updateWebhook = async ({id, ...body}: UpdateWebhookPayload) => {
  const payload = Object.fromEntries(
    Object.entries(body).filter(([_, v]) => v !== undefined)
  );
  const {data} = await api.patch<Webhook>(
    `/integrations/webhook-configs/${id}/`,
    payload
  );
  return data;
};

const deleteWebhook = async (id: string | undefined) => {
  if (!id) return;
  const {data} = await api.delete(`/integrations/webhook-configs/${id}/`);
  return data;
};

const regenerateSecret = async (id: string) => {
  const {data} = await api.post<Webhook>(
    `/integrations/webhook-configs/${id}/regenerate-secret/`
  );
  return data;
};

const WEBHOOK_LIST_SCHEMA = [
  {
    dataKey: 'name',
    header: 'Name',
    sortable: true,
    index: 0,
  },
  {
    dataKey: 'url',
    header: 'Endpoint URL',
    sortable: true,
    index: 1,
  },
  {
    dataKey: 'events',
    header: 'Events',
    sortable: false,
    index: 2,
  },
  {
    dataKey: 'is_active',
    header: 'Status',
    sortable: true,
    index: 3,
  },
  {
    dataKey: 'created_at',
    header: 'Created',
    sortable: true,
    index: 4,
  },
];

export const useWebhookSettings = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);
  const [isSecretModalOpen, setIsSecretModalOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);

  const {
    data: webhooks,
    refetch,
    isLoading: isLoadingWebhooks,
  } = useQuery({
    queryKey: ['webhooks'],
    queryFn: getWebhookList,
  });

  const {data: webhookEvents, isLoading: isLoadingEvents} = useQuery({
    queryKey: ['webhook-events'],
    queryFn: getWebhookEvents,
  });

  const {mutateAsync: createWebhookMutation, isLoading: isCreating} =
    useMutation({
      mutationFn: createWebhook,
      onSuccess: (data) => {
        toast.success('Webhook created successfully');
        refetch();
        if (data?.secret) {
          setCreatedSecret(data.secret);
          setIsSecretModalOpen(true);
        }
      },
      onError: (error) => {
        const message = get(
          error,
          'response.data.detail',
          'Failed to create webhook'
        );
        toast.error(message);
      },
      onSettled: () => {
        setIsCreateModalOpen(false);
      },
    });

  const {mutateAsync: updateWebhookMutation, isLoading: isUpdating} =
    useMutation({
      mutationFn: updateWebhook,
      onSuccess: (data) => {
        toast.success('Webhook updated successfully');
        refetch();
        if (data) setSelectedWebhook(data);
      },
      onError: (error) => {
        const message = get(
          error,
          'response.data.detail',
          'Failed to update webhook'
        );
        toast.error(message);
      },
    });

  const {mutateAsync: deleteWebhookMutation, isLoading: isDeleting} =
    useMutation({
      mutationFn: deleteWebhook,
      onSuccess: () => {
        toast.success('Webhook deleted successfully');
        refetch();
      },
      onError: (error) => {
        const message = get(
          error,
          'response.data.detail',
          'Failed to delete webhook'
        );
        toast.error(message);
      },
      onSettled: () => {
        setIsDeleteModalOpen(false);
        setSelectedWebhook(null);
      },
    });

  const {mutateAsync: regenerateSecretMutation, isLoading: isRegenerating} =
    useMutation({
      mutationFn: regenerateSecret,
      onSuccess: (data) => {
        toast.success('Secret regenerated successfully');
        setGeneratedSecret(data?.secret ?? null);
      },
      onError: (error) => {
        const message = get(
          error,
          'response.data.detail',
          'Failed to regenerate secret'
        );
        toast.error(message);
      },
    });

  const handleToggleActive = useCallback(
    async (id: string, checked: boolean) => {
      await updateWebhookMutation({id, is_active: checked});
    },
    [updateWebhookMutation]
  );

  const handleRowClick = useCallback(
    (item: DataItem) => {
      const webhook = (webhooks?.results || []).find((w) => w.id === item.id);
      if (webhook) {
        setGeneratedSecret(null);
        setSelectedWebhook(webhook);
        setIsDetailModalOpen(true);
      }
    },
    [webhooks]
  );

  const handleDeleteWebhook = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const eventOptions = (webhookEvents || []).map((event) => ({
    label: event.label,
    value: event.key,
  }));

  return {
    data: webhooks?.results || [],
    isLoadingWebhooks,
    WEBHOOK_LIST_SCHEMA,
    isCreateModalOpen,
    setIsCreateModalOpen,
    createWebhookMutation,
    isCreating,
    eventOptions,
    isLoadingEvents,
    updateWebhookMutation,
    isUpdating,
    handleToggleActive,
    isDetailModalOpen,
    setIsDetailModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedWebhook,
    handleRowClick,
    handleDeleteWebhook,
    deleteWebhookMutation,
    isDeleting,
    regenerateSecretMutation,
    isRegenerating,
    generatedSecret,
    isSecretModalOpen,
    setIsSecretModalOpen,
    createdSecret,
  };
};
